import * as d3 from 'd3';
import { basedir } from '../lib/helpers'

addEventListener('DOMContentLoaded', () => {
	const dashboard = document.querySelector('div.dashboard')
	if (dashboard) {
		drawGraphs(dashboard)
	}
	const graphSelection = document.querySelector('div.graphs_selection')
	if (graphSelection) {
		graphSelectionDropDown(graphSelection)
	}
})

function graphSelectionDropDown(graphSelection: Element) {

	const totals = graphSelection.querySelector('div.totals')
	if (totals) {
		totals.addEventListener('click', () => {
			graphSelection.classList.toggle('open')

			document.addEventListener('click', (event) => {
				if (!graphSelection.contains(event.target as HTMLDivElement)) {
					graphSelection.classList.remove('open')
				}
			});
		})
	}

	const checkboxes = graphSelection.querySelectorAll('input[type=checkbox]')
	checkboxes.forEach((checkbox) => {
		checkbox.addEventListener('change', () => {
			const selectedCheckboxes = Array.from(checkboxes)
				.filter((c): c is HTMLInputElement => (c as HTMLInputElement).checked)

			const span = totals.querySelector('span')
			if (span) {
				span.innerHTML = `${selectedCheckboxes.length}`
			}
		})
	})
}

function drawGraphs(dashboard: Element) {

	// Main function to load data and render the chart
	async function renderChart() {

		try {
			const data = await fetchData();

			data.forEach((graph) => {
				createBarChart(graph, 'dashboard_' + graph.type);
			})
		} catch (error) {
			console.error("Error fetching or rendering chart data:", error);
		}
	}
	renderChart()

	// Define the structure of the data you expect from the API
	interface ValueItem {
		value: string;
		name: string;
		date: string;
		count: number;
	}

	interface TotalItem {
		date: string;
		count: number;
	}

	interface DataItem {
		type: string;
		info: string;
		values: ValueItem[];
		totals: TotalItem[];
	}

	// Function to fetch data from an API
	async function fetchData(): Promise<DataItem[]> {

		const url = basedir() + `/api/cms/dashboard/d3`
		const body = createAPIBody()

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(body)
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch data: ${response.statusText}`);
		}

		return response.json();
	}

	// Function to create a bar chart
	function createBarChart(data: DataItem, elementId: string): void {
		const width = 500;
		const height = 300;
		const margin = { top: 20, right: 30, bottom: 50, left: 40 };

		const tooltip = d3.select(`#tooltip`);

		// Select the DOM element
		const svg = d3.select(`#${elementId}`)
			.append("svg")
			.attr("width", width)
			.attr("height", height);

		// Set up scales
		const x = d3.scaleBand()
			.domain(data.totals.map(d => d.date))
			.range([margin.left, width - margin.right])
			.padding(0.1);

		const y = d3.scaleLinear()
			.domain([0, d3.max(data.totals, d => d.count) || 0])
			.nice()
			.range([height - margin.bottom, margin.top]);

		let timer

		// Add bars to the chart
		svg.append("g")
			.selectAll("rect")
			.data(data.totals)
			.enter().append("rect")
			.attr("x", d => x(d.date)!)
			.attr("y", d => y(d.count))
			.attr("height", d => y(0) - y(d.count))
			.attr("width", x.bandwidth())
			.attr("fill", "rgb(81, 165, 187)")
			.on("mouseover", function (event, d) {
				d3.select(this).attr("fill", "rgb(229, 38, 41)");
				tooltip.style("visibility", "visible")
					.html(`<span class="count">${d.count}</span><span class="date">${d.date}</span>${tooltipDetails(d.date, data.values)}`);
				clearTimeout(timer)
			})
			.on("mousemove", (event) => {
				tooltip.style("top", `${event.pageY - 150}px`)
					.style("left", `${event.pageX + 10}px`);
			})
			.on("mouseout", function () {
				d3.select(this).attr("fill", "rgb(81, 165, 187)");
				timer = setTimeout(function () { tooltip.style("visibility", "hidden"); }, 250)
			});

		// Calculate the step to show only 10 x-axis labels
		const tickValues = data.totals.map(d => d.date).filter((_, i) => i % Math.ceil(data.totals.length / 20) === 0);

		// Add X-axis with limited labels
		svg.append("g")
			.attr("transform", `translate(0,${height - margin.bottom})`)
			.call(d3.axisBottom(x).tickValues(tickValues).tickSizeOuter(0))
			.selectAll("text")  // Select all tick text elements
			.attr("transform", "rotate(-45)")  // Rotate 45 degrees
			.style("text-anchor", "end");  // Align text to start for better readability

		// Add Y-axis
		svg.append("g")
			.attr("transform", `translate(${margin.left},0)`)
			.call(d3.axisLeft(y));
	}

	function tooltipDetails(date: string, values: ValueItem[]): string {
		const rows = values.filter(v => { return v.date == date })

		if (rows.length == 0) {
			return ''
		}

		rows.sort((a, b) => {
			if (a.name < b.name) return -1;
			if (a.name > b.name) return 1;
			return 0;
		});

		let html = '';
		rows.forEach(row => {
			html += `<tr><td>${row.name}</td><td>${row.count}</td></tr>`
		})
		return '<table><tbody>' + html + '</tbody></table>';
	}

	function createAPIBody(): object {

		let types = [];
		const graphs = dashboard.querySelectorAll('div.graph')
		graphs.forEach((graph) => {
			types.push(graph.getAttribute('data-type'))
		})

		const body = {
			project_uuid: (dashboard.querySelector('input[name=project_id]') as HTMLInputElement).value,
			date_start: (dashboard.querySelector('input[name=date_start]') as HTMLInputElement).value,
			date_end: (dashboard.querySelector('input[name=date_end]') as HTMLInputElement).value,
			time_interval: (dashboard.querySelector('input[name=time_interval]') as HTMLInputElement).value,
			types: types
		}

		return body
	}


}